import type useEmblaCarousel from 'embla-carousel-react';
import type { UseEmblaCarouselType } from 'embla-carousel-react';

// embla-carousel is not a direct dependency — derive the api and plugin types
// through embla-carousel-react's hook signature instead.
type EmblaCarouselType = NonNullable<UseEmblaCarouselType[1]>;
type EmblaPluginType = NonNullable<
    Parameters<typeof useEmblaCarousel>[1]
>[number];

/**
 * How far past the edge the rail can stretch before resistance saturates, as a
 * percentage of the viewport. Embla's built-in ScrollBounds uses 50 — half a
 * viewport of give, which reads as loose — while 7 caps the visible stretch
 * at the small, firm nudge of Apple's product-page rails.
 */
const OVERSCROLL_TOLERANCE_PERCENT = 7;
/** Remaining correction below which the spring settles exactly on the edge. */
const PULL_BACK_THRESHOLD_PERCENT = 10;
const FRICTION_MIN = 0.1;
const FRICTION_MAX = 0.99;
/** Settle animation length (frames) once the pull-back threshold is reached.
 *  Embla's own bounds settle uses 25; shorter reads as the snappier Apple
 *  spring. */
const SETTLE_DURATION = 16;

/**
 * Re-tunes embla's edge overscroll to a subtle, Apple-like rubber band: the
 * same proportional-friction spring embla ships in ScrollBounds, with the
 * stretch tolerance tightened so dragging or flinging past the first/last
 * snap gives a small resisted offset that springs straight back.
 *
 * Implemented by replacing `engine.scrollBounds.constrain` — the engine calls
 * it every physics tick (non-loop carousels only) and its collaborators
 * (limit, location, target, scrollBody) are all part of embla's typed engine
 * surface. Re-init re-runs `init` against the fresh engine, so the tuning
 * survives resizes and option changes.
 */
export function SubtleOverscroll(): EmblaPluginType {
    function init(embla: EmblaCarouselType): void {
        const engine = embla.internalEngine();
        const {
            limit,
            location,
            target,
            scrollBody,
            scrollBounds,
            percentOfView,
        } = engine;
        const tolerance = percentOfView.measure(OVERSCROLL_TOLERANCE_PERCENT);
        const pullBackThreshold = percentOfView.measure(
            PULL_BACK_THRESHOLD_PERCENT,
        );

        scrollBounds.constrain = (pointerDown: boolean) => {
            if (!scrollBounds.shouldConstrain()) return;
            const edge = limit.reachedMin(location.get()) ? 'min' : 'max';
            const diffToEdge = Math.abs(limit[edge] - location.get());
            const diffToTarget = target.get() - location.get();
            const friction = Math.min(
                FRICTION_MAX,
                Math.max(FRICTION_MIN, diffToEdge / tolerance),
            );
            target.subtract(diffToTarget * friction);
            if (!pointerDown && Math.abs(diffToTarget) < pullBackThreshold) {
                target.set(limit.constrain(target.get()));
                scrollBody.useDuration(SETTLE_DURATION).useBaseFriction();
            }
        };
    }

    function destroy(): void {
        // The patched engine is discarded with the embla instance.
    }

    return { name: 'subtleOverscroll', options: {}, init, destroy };
}
